import React, { useState, useEffect, useCallback, useMemo } from 'react';
import OutfitAdminLayout from './OutfitAdminLayout';
import { outfitAdminApi } from '../../../services/outfitAdminApi';

function CCIBlacklist({ email, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [cciList, setCciList] = useState([]);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  // Filters
  const [genderFilter, setGenderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [occasionFilter, setOccasionFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const fetchCCIs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await outfitAdminApi.fetchCCIBlacklist();
      if (response && Array.isArray(response.data)) {
        setCciList(response.data);
      }
    } catch (err) {
      console.error('Failed to load CCI blacklist:', err);
      setError(err.message || 'Failed to load CCI blacklist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCCIs();
  }, [fetchCCIs]);

  // Derived metrics
  const metrics = useMemo(() => {
    const total = cciList.length;
    let active = 0;
    let discontinued = 0;
    for (const item of cciList) {
      if (item.status === 'active') active += 1;
      else if (item.status === 'discontinued') discontinued += 1;
    }
    return { total, active, discontinued };
  }, [cciList]);

  // Unique occasions for filter dropdown
  const allOccasions = useMemo(() => {
    const set = new Set();
    for (const item of cciList) {
      if (Array.isArray(item.occasions)) {
        item.occasions.forEach((occ) => set.add(occ));
      }
    }
    return Array.from(set).sort();
  }, [cciList]);

  // Filtered list
  const filteredCCIs = useMemo(() => {
    return cciList.filter((item) => {
      // Gender filter
      if (genderFilter !== 'all' && item.gender !== genderFilter) {
        return false;
      }
      // Status filter
      if (statusFilter !== 'all' && item.status !== statusFilter) {
        return false;
      }
      // Occasion filter
      if (occasionFilter && Array.isArray(item.occasions)) {
        if (!item.occasions.some((occ) => occ.toLowerCase().includes(occasionFilter.toLowerCase()))) {
          return false;
        }
      }
      // Search query (CCI hash or category names)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const hashMatch = item.category_combination_id?.toLowerCase().includes(q);
        let categoryMatch = false;
        if (item.combination) {
          categoryMatch = Object.values(item.combination).some((val) => {
            if (Array.isArray(val)) {
              return val.some((subVal) => String(subVal).toLowerCase().includes(q));
            }
            return String(val).toLowerCase().includes(q);
          });
        }
        if (!hashMatch && !categoryMatch) {
          return false;
        }
      }
      return true;
    });
  }, [cciList, genderFilter, statusFilter, occasionFilter, searchQuery]);

  // Paginated list
  const totalPages = Math.ceil(filteredCCIs.length / itemsPerPage) || 1;
  const paginatedCCIs = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredCCIs.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredCCIs, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [genderFilter, statusFilter, occasionFilter, searchQuery]);

  // Handle Copy Hash
  const handleCopyHash = (hash) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(hash);
      setCopiedId(hash);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // Handle Status Toggle (Optimistic Update)
  const handleToggleStatus = async (item) => {
    const newStatus = item.status === 'active' ? 'discontinued' : 'active';
    const cciId = item.category_combination_id;

    // Optimistic local update
    setCciList((prevList) =>
      prevList.map((doc) =>
        doc.category_combination_id === cciId ? { ...doc, status: newStatus } : doc
      )
    );

    try {
      await outfitAdminApi.updateCCIStatus(cciId, newStatus);
      setSuccessMsg(`CCI ${cciId.slice(0, 8)}... updated to ${newStatus}.`);
    } catch (err) {
      console.error('Failed to update CCI status:', err);
      setError(err.message || 'Failed to update status');
      // Revert optimistic update
      setCciList((prevList) =>
        prevList.map((doc) =>
          doc.category_combination_id === cciId ? { ...doc, status: item.status } : doc
        )
      );
    }
  };

  return (
    <OutfitAdminLayout
      email={email}
      onLogout={onLogout}
      pageTitle="Category Combination ID (CCI) Blacklist"
    >
      {error && (
        <div className="outfit-alert error">
          <span>{error}</span>
          <button className="outfit-btn outfit-btn-secondary" onClick={() => setError('')}>
            ✕
          </button>
        </div>
      )}

      {successMsg && (
        <div className="outfit-alert success">
          <span>{successMsg}</span>
          <button className="outfit-btn outfit-btn-secondary" onClick={() => setSuccessMsg('')}>
            ✕
          </button>
        </div>
      )}

      {/* Summary Metrics Bar */}
      <div className="cci-metrics-bar">
        <div className="cci-metric-card">
          <span className="cci-metric-label">Total Formulas</span>
          <span className="cci-metric-value">{metrics.total}</span>
        </div>
        <div className="cci-metric-card active">
          <span className="cci-metric-label">Active CCIs</span>
          <span className="cci-metric-value">{metrics.active}</span>
        </div>
        <div className="cci-metric-card discontinued">
          <span className="cci-metric-label">Discontinued CCIs</span>
          <span className="cci-metric-value">{metrics.discontinued}</span>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="cci-filter-toolbar">
        {/* Gender Filter */}
        <div className="kb-selector-group">
          <label htmlFor="cci-gender-filter">Gender:</label>
          <select
            id="cci-gender-filter"
            className="kb-select"
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
          >
            <option value="all">All Genders</option>
            <option value="men">Men</option>
            <option value="women">Women</option>
          </select>
        </div>

        {/* Status Filter */}
        <div className="kb-selector-group">
          <label htmlFor="cci-status-filter">Status:</label>
          <select
            id="cci-status-filter"
            className="kb-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </div>

        {/* Occasion Filter */}
        <div className="kb-selector-group">
          <label htmlFor="cci-occasion-filter">Occasion:</label>
          <select
            id="cci-occasion-filter"
            className="kb-select"
            value={occasionFilter}
            onChange={(e) => setOccasionFilter(e.target.value)}
          >
            <option value="">All Occasions</option>
            {allOccasions.map((occ) => (
              <option key={occ} value={occ}>
                {occ}
              </option>
            ))}
          </select>
        </div>

        {/* Search Input */}
        <input
          type="text"
          className="cci-search-input"
          placeholder="Search by CCI ID hash or category name (e.g. Jeans, Kurtas)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="outfit-loading-state">
          <div className="outfit-spinner" />
          <p>Loading category combination documents...</p>
        </div>
      ) : filteredCCIs.length === 0 ? (
        <div className="outfit-empty-state">
          <p>No category combinations found matching your filters.</p>
        </div>
      ) : (
        <div className="cci-table-wrapper">
          <table className="cci-table">
            <thead>
              <tr>
                <th style={{ width: '22%' }}>CCI ID & Gender</th>
                <th style={{ width: '45%' }}>Category Combination Formula</th>
                <th style={{ width: '20%' }}>Occasions</th>
                <th style={{ width: '13%', textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCCIs.map((item) => {
                const isItemActive = item.status === 'active';
                const cciId = item.category_combination_id || '';
                const shortId = cciId.length > 12 ? `${cciId.slice(0, 10)}...` : cciId;

                return (
                  <tr key={item._id || cciId}>
                    <td>
                      <div className="cci-id-cell">
                        <div className="cci-id-hash" title={cciId}>
                          <span>{shortId}</span>
                          <button
                            className="cci-copy-btn"
                            title="Copy full hash"
                            onClick={() => handleCopyHash(cciId)}
                          >
                            {copiedId === cciId ? (
                              <span style={{ color: '#16a34a', fontSize: '11px', fontWeight: 'bold' }}>✓</span>
                            ) : (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <span className={`cci-gender-badge ${item.gender}`}>
                          {item.gender}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="cci-formula-grid">
                        {item.combination &&
                          Object.entries(item.combination).map(([slot, categoryVal]) => {
                            const valStr = Array.isArray(categoryVal)
                              ? categoryVal.join(', ')
                              : categoryVal;
                            return (
                              <span key={slot} className="cci-slot-pill">
                                <span className="cci-slot-label">{slot.replace(/_/g, ' ')}:</span>
                                <span className="cci-slot-value">{valStr}</span>
                              </span>
                            );
                          })}
                      </div>
                    </td>

                    <td>
                      <div className="cci-occasions-wrap">
                        {Array.isArray(item.occasions) && item.occasions.length > 0 ? (
                          item.occasions.map((occ, oIdx) => (
                            <span key={oIdx} className="cci-occasion-tag">
                              {occ}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                        )}
                      </div>
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <label className="cci-status-switch">
                          <input
                            type="checkbox"
                            checked={isItemActive}
                            onChange={() => handleToggleStatus(item)}
                          />
                          <span className="cci-slider" />
                        </label>
                        <span
                          className={`cci-status-label ${
                            isItemActive ? 'active' : 'discontinued'
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderTop: '1px solid #e2e8f0',
                background: '#f8fafc',
              }}
            >
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredCCIs.length)} of {filteredCCIs.length} CCIs
              </span>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="outfit-btn outfit-btn-secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                >
                  &lt; Prev
                </button>
                <span style={{ fontSize: '13px', fontWeight: '600', padding: '0 8px' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="outfit-btn outfit-btn-secondary"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                >
                  Next &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </OutfitAdminLayout>
  );
}

export default CCIBlacklist;
